<script setup lang="ts">
/**
 * TexteRepliable — texte saisi par l'association, replié à quelques lignes
 * avec « … » et un bouton « Voir plus / Voir moins » animé.
 *
 * Né sur l'en-tête de la page /pay/[slug] (message du QR code, description du
 * lien de paiement) : sans repli, un texte de 20 lignes recouvrait la bannière
 * — montant, titre illisibles — ou repoussait le récapitulatif loin sous la
 * ligne de flottaison (Karim 2026-10-04).
 *
 * Le bouton n'apparaît QUE si le texte est réellement coupé : mesuré sur la
 * page (scrollHeight > clientHeight), pas deviné au nombre de caractères — la
 * largeur change tout (mobile, bannière, police).
 *
 * Taille, couleur et style du texte viennent du parent : ce composant ne gère
 * que le repli, l'animation et le bouton.
 */

interface Props {
  texte: string;
  /** Lignes visibles une fois replié. */
  lignes: 3 | 4;
  /** `blanc` sur une bannière assombrie, `noir` sur fond clair (Karim 2026-10-04). */
  couleurBouton: "blanc" | "noir";
}

const props = defineProps<Props>();

const texteEl = ref<HTMLElement | null>(null);
/** Boîte dont la hauteur est animée — le <p> dedans garde sa hauteur réelle. */
const cadre = ref<HTMLElement | null>(null);
/** État voulu (libellé du bouton, aria-expanded) — bascule dès le clic. */
const deplie = ref(false);
/**
 * `line-clamp` posé ? Distinct de `deplie` : à la fermeture, on le remet
 * seulement QUAND le cadre a fini de se refermer — le remettre au clic ferait
 * sauter le texte d'un coup à 3-4 lignes avant que la boîte ne bouge.
 */
const replie = ref(true);
const coupe = ref(false);

function mesurer() {
  const el = texteEl.value;
  // Déplié (ou en train de se replier), le texte n'est plus coupé : on garde
  // le dernier verdict pour que « Voir moins » reste affiché.
  if (!el || !replie.value) return;
  coupe.value = el.scrollHeight - el.clientHeight > 1;
}

let observateur: ResizeObserver | null = null;

onMounted(() => {
  mesurer();
  // Rotation d'écran, police chargée après coup, colonne redimensionnée :
  // la coupure change avec la largeur.
  if (texteEl.value && typeof ResizeObserver !== "undefined") {
    observateur = new ResizeObserver(mesurer);
    observateur.observe(texteEl.value);
  }
});

onBeforeUnmount(() => observateur?.disconnect());

function finAnimation() {
  if (!deplie.value) replie.value = true;
  // Hauteur libre : le texte suit à nouveau la largeur (rotation, zoom).
  if (cadre.value) cadre.value.style.height = "";
}

watch(
  () => props.texte,
  () => {
    deplie.value = false;
    finAnimation();
    nextTick(mesurer);
  },
);

function mouvementReduit() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

/**
 * Ouverture / fermeture animées : `line-clamp` ne s'anime pas, on anime donc
 * la HAUTEUR du cadre entre deux valeurs mesurées en pixels (une hauteur
 * `auto` ne s'anime pas non plus). Départ = hauteur affichée à cet instant :
 * un second clic en pleine animation repart de là, sans saut.
 */
async function basculer() {
  const boite = cadre.value;
  const el = texteEl.value;
  if (!boite || !el || mouvementReduit()) {
    deplie.value = !deplie.value;
    replie.value = !deplie.value;
    return;
  }

  const depart = boite.getBoundingClientRect().height;
  boite.style.height = `${depart}px`;

  let arrivee: number;
  if (!deplie.value) {
    deplie.value = true;
    replie.value = false;
    await nextTick();
    arrivee = el.scrollHeight;
  } else {
    deplie.value = false;
    // Hauteur exacte du texte replié : N lignes de sa hauteur de ligne.
    arrivee =
      parseFloat(window.getComputedStyle(el).lineHeight) * props.lignes ||
      depart;
  }

  // Rien à animer : `transitionend` ne viendrait jamais, on conclut tout de suite.
  if (Math.abs(arrivee - depart) < 1) {
    finAnimation();
    return;
  }
  // Force le navigateur à prendre en compte la hauteur de départ avant la
  // nouvelle, sinon il saute directement à l'arrivée sans transition.
  void boite.offsetHeight;
  boite.style.height = `${arrivee}px`;
}

function surFinTransition(e: TransitionEvent) {
  if (e.target !== cadre.value || e.propertyName !== "height") return;
  finAnimation();
}

/**
 * Replié, les lignes vides entre paragraphes sont resserrées : sinon la
 * dernière ligne visible tombait souvent sur une ligne vide et le « … »
 * s'affichait tout seul, en gaspillant une des 3-4 lignes (Karim 2026-10-04).
 * Déplié, le texte est rendu tel que l'association l'a écrit.
 */
const texteAffiche = computed(() =>
  replie.value ? props.texte.replace(/\n\s*\n/g, "\n") : props.texte,
);

// `line-clamp-*` posé sur le <p> nu — jamais avec `block`/`flex` sur le même
// élément, leur `display` annulerait la troncature. Classes écrites en entier
// pour que Tailwind les génère. `whitespace-pre-line` : l'association écrit
// sur plusieurs lignes, sans quoi tout se colle en un seul paragraphe.
const classeTexte = computed(() => {
  if (!replie.value) return "whitespace-pre-line";
  return props.lignes === 3
    ? "whitespace-pre-line line-clamp-3"
    : "whitespace-pre-line line-clamp-4";
});
</script>

<template>
  <div>
    <!-- Même courbe que le « Voir plus » du widget (DonateEmbedApp). -->
    <div
      ref="cadre"
      class="overflow-hidden transition-[height] duration-[350ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none"
      @transitionend="surFinTransition"
    >
      <p ref="texteEl" :class="classeTexte">{{ texteAffiche }}</p>
    </div>
    <button
      v-if="coupe"
      type="button"
      :class="[
        'mt-1.5 text-sm font-semibold not-italic underline underline-offset-2 transition-opacity hover:opacity-80',
        couleurBouton === 'blanc' ? 'text-white' : 'text-gray-900',
      ]"
      :aria-expanded="deplie"
      @click="basculer"
    >
      {{ deplie ? "Voir moins" : "Voir plus" }}
    </button>
  </div>
</template>
